import { yupResolver } from '@hookform/resolvers/yup';
import ButtonOutline from 'components/common/button/outline';
import Modal from 'components/common/modal';
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSeparator,
    InputOTPSlot,
} from 'components/common/otp-input';
import { useModal } from 'hooks/use-modal';
import { TFunction } from 'i18next';
import { paths } from 'lib/constants/paths';
import React, { useEffect, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    usePostApiV1ClientAdsMutation,
    usePostApiV1ClientIdentityVerifyOtpForAdsMutation,
} from 'service/api/allApis';
import { toast } from 'sonner';
import * as yup from 'yup';

const getSchema = (t: TFunction<'translation', undefined>) =>
    yup
        .object({
            phoneNumber: yup.string(),
            otpCode: yup
                .string()
                .required(t('validation.field-required', { field: t('auth.otp') }))
                .min(4, t('validation.invalid-field', { field: t('auth.otp') }))
                .max(4, t('validation.invalid-field', { field: t('auth.otp') })),
        })
        .required();

type FormValues = {
    phoneNumber?: string;
    otpCode: string;
};

const AdvertOtpModal = () => {
    const { isOpen, type, onClose, data: modalData } = useModal();
    const [createMutation, { isLoading: isCreateLoading }] = usePostApiV1ClientAdsMutation();
    const [verifyOtpMutation, { isLoading: verifyOtpLoading }] =
        usePostApiV1ClientIdentityVerifyOtpForAdsMutation();
    const otpInputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();
    const { createData } = modalData;
    const advertOtpNumber = createData?.phoneNumber;
    const formData = createData?.formData;
    const isLoading = isCreateLoading || verifyOtpLoading;
    const navigate = useNavigate();

    const { watch, setValue, handleSubmit } = useForm<FormValues>({
        resolver: yupResolver(getSchema(t)),
    });
    const isModalOpen = isOpen && type == 'advertOtp';

    const onSubmit: SubmitHandler<FormValues> = (values) => {
        verifyOtpMutation({
            otpCode: Number(values.otpCode),
            phoneNumber: values.phoneNumber,
        })
            .unwrap()
            .then(() => {
                return createMutation({
                    body: formData as any,
                }).unwrap();
            })
            .then(() => {
                toast.success(t('success.advert-create'));
                navigate(paths.home);
            })
            .catch((e) => {
                const errorMessage =
                    e.data?.message ?? e?.data?.Title ?? t('general.something-went-wrong');
                toast.error(errorMessage);
            });
    };

    useEffect(() => {
        setValue('phoneNumber', advertOtpNumber);
    }, [advertOtpNumber]);

    return (
        <Modal
            isOpen={isModalOpen}
            onClose={onClose}
            modalContainerClassName="flex"
            modalClassName="w-full max-w-[400px] mx-6  bg-white rounded-2xl p-4 md:p-8"
        >
            <h2 className="text-[32px] font-bold text-txt">{t('advert.advert-otp-title')}</h2>
            <div className="mt-5 mb-8 ">
                <p className="text-xs text-txt-60 font-medium">
                    {t('auth.otp-sent-to', {
                        phone: watch('phoneNumber'),
                    })}
                    .
                </p>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col gap-y-8 pb-6">
                <InputOTP
                    maxLength={4}
                    ref={otpInputRef}
                    className="mx-auto"
                    onChange={(value) => setValue('otpCode', value)}
                >
                    <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                    </InputOTPGroup>
                    <InputOTPSeparator />
                    <InputOTPGroup>
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                    </InputOTPGroup>
                </InputOTP>
                <ButtonOutline
                    loading={isLoading}
                    text={t('auth.verify')}
                    className="rounded-sm text-txt-60 border border-txt-60"
                />
            </form>
        </Modal>
    );
};

export default AdvertOtpModal;
